import { useNavigate, useSearch } from '@tanstack/react-router';
import { Loader2, LockKeyhole } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { AuthCard } from '@/components/layout/AuthCard';
import { Button } from '@/components/ui/button';
import { FormAlert } from '@/components/ui/form-alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { genericErrorMessage, httpStatus, serverMessage } from '@/lib/api-errors';
import { authApi } from '@/lib/auth-api';
import { useChallenge } from '@/stores/challenge';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const loginErrorMessage = (err: unknown): string => {
  if (httpStatus(err) === 401) {
    // platform-api ne dit « compte suspendu » qu'après un mot de passe correct : rien n'est révélé à un tiers
    return serverMessage(err) === 'compte suspendu'
      ? 'Ce compte administrateur est suspendu'
      : 'Email ou mot de passe incorrect';
  }
  return genericErrorMessage(err);
};

interface FieldErrors {
  email?: string;
  password?: string;
}

export const validateLogin = (email: string, password: string): FieldErrors => {
  const errors: FieldErrors = {};
  if (!email.trim()) errors.email = "L'email est obligatoire";
  else if (!EMAIL_RE.test(email.trim())) errors.email = "Format d'email invalide";
  if (!password) errors.password = 'Le mot de passe est obligatoire';
  return errors;
};

export const LoginPage = () => {
  const navigate = useNavigate();
  const { reason } = useSearch({ from: '/login' });
  const setChallenge = useChallenge((s) => s.setChallenge);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const errors = validateLogin(email, password);
    setFieldErrors(errors);
    if (errors.email || errors.password) return;

    setLoading(true);
    try {
      const { challengeToken } = await authApi.login(email.trim(), password);
      setChallenge(challengeToken, email.trim());
      void navigate({ to: '/mfa-verify' });
    } catch (err) {
      setError(loginErrorMessage(err));
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard
      icon={LockKeyhole}
      title="Connexion administrateur"
      description="Accès réservé aux administrateurs de la plateforme LOGISTECH-LGT."
      footer="Une vérification en deux étapes (code TOTP) suit la saisie du mot de passe."
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        {reason === 'expired' && !error && <FormAlert tone="info">Session expirée, reconnectez-vous</FormAlert>}
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoFocus
            placeholder="admin@logistech-lgt.com"
            aria-invalid={Boolean(fieldErrors.email)}
            aria-describedby={fieldErrors.email ? 'email-error' : undefined}
          />
          {fieldErrors.email && <p id="email-error" className="text-xs text-danger">{fieldErrors.email}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Mot de passe</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            aria-invalid={Boolean(fieldErrors.password)}
            aria-describedby={fieldErrors.password ? 'password-error' : undefined}
          />
          {fieldErrors.password && <p id="password-error" className="text-xs text-danger">{fieldErrors.password}</p>}
        </div>
        {error && <FormAlert>{error}</FormAlert>}
        <Button type="submit" className="w-full" size="lg" disabled={loading}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
          {loading ? 'Connexion…' : 'Se connecter'}
        </Button>
      </form>
    </AuthCard>
  );
};
